import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  UserCircle,
  Phone,
  Mail,
  Calendar,
  MoreVertical,
  Edit,
  Trash2,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { memberTitles, currencySymbols, PLAN_MEMBER_LIMITS } from '@/types';
import type { Member, SubscriptionPlan } from '@/types';
import { format } from 'date-fns';
import { sendMemberLimitNotification } from '@/lib/notifications';

export default function Members() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization, setMembers, members, addMember, removeMember, user } = useAppStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('Member');
  const [joinDate, setJoinDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  useEffect(() => {
    fetchMembers();
  }, [currentOrganization]);

  const fetchMembers = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('full_name');

      if (error) throw error;
      
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!fullName.trim()) {
      toast.error('Full name is required');
      return;
    }

    if (!currentOrganization) return;

    // Cap enforcement
    const plan = (currentOrganization.subscription_plan ?? 'free') as SubscriptionPlan;
    const limit = PLAN_MEMBER_LIMITS[plan];
    if (members.length >= limit) {
      toast.error(
        `You've reached the ${limit}-member limit for the ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan. Please upgrade to add more members.`
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('members')
        .insert({
          organization_id: currentOrganization.id,
          full_name: fullName.trim(),
          phone: phone || null,
          email: email || null,
          title: title || 'Member',
          join_date: joinDate,
          status,
          dues_balance: 0,
          credit_balance: 0,
        })
        .select()
        .single();

      if (error) throw error;

      addMember(data);
      toast.success(t('members.createSuccess'));

      // Check member limit thresholds and notify org admin
      if (user && currentOrganization) {
        const plan = (currentOrganization.subscription_plan ?? 'free') as SubscriptionPlan;
        const max = PLAN_MEMBER_LIMITS[plan];
        const prevCount = members.length;
        const newCount = prevCount + 1;
        const threshold80 = Math.floor(max * 0.8);
        if (newCount >= max) {
          await sendMemberLimitNotification(user.id, currentOrganization.id, newCount, max);
        } else if (newCount >= threshold80 && prevCount < threshold80) {
          await sendMemberLimitNotification(user.id, currentOrganization.id, newCount, max);
        }
      }

      setIsAddDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMember = async (member: Member) => {
    if (!confirm(t('members.deleteConfirm'))) return;

    try {
      const { error } = await supabase
        .from('members')
        .delete()
        .eq('id', member.id);

      if (error) throw error;

      removeMember(member.id);
      toast.success(t('members.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    }
  };

  const resetForm = () => {
    setFullName('');
    setPhone('');
    setEmail('');
    setTitle('Member');
    setJoinDate(format(new Date(), 'yyyy-MM-dd'));
    setStatus('active');
  };

  const filteredMembers = members.filter((member) =>
    member.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.phone?.includes(searchQuery) ||
    member.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('navigation.members')}</h1>
          <p className="text-muted-foreground">
            {members.length} / {PLAN_MEMBER_LIMITS[(currentOrganization?.subscription_plan ?? 'free') as SubscriptionPlan]} members
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('members.add')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={t('members.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Members Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredMembers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.map((member) => (
            <Card key={member.id} className="group hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div 
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/members/${member.id}`)}
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserCircle className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{member.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{member.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={member.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                          {member.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/members/${member.id}`)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteMember(member)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-4 pt-4 border-t space-y-2">
                  {member.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span>{member.phone}</span>
                    </div>
                  )}
                  {member.email && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{member.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {format(new Date(member.join_date), 'MMM dd, yyyy')}</span>
                  </div>
                </div>

                {((member.dues_balance || 0) > 0 || (member.credit_balance || 0) > 0) && (
                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    {(member.dues_balance || 0) > 0 && (
                      <span className="text-sm text-red-600">
                        Owes: {currencySymbol}{member.dues_balance || 0}
                      </span>
                    )}
                    {(member.credit_balance || 0) > 0 && (
                      <span className="text-sm text-green-600">
                        Credit: {currencySymbol}{member.credit_balance || 0}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <UserCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {searchQuery ? 'No members found' : t('members.noMembers')}
          </h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Try a different search term' : t('members.addFirst')}
          </p>
          {!searchQuery && (
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('members.add')}
            </Button>
          )}
        </div>
      )}

      {/* Add Member Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('members.add')}</DialogTitle>
            <DialogDescription>
              Add a new member to your organization
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddMember} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">{t('members.fullName')} *</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">{t('members.title_label')}</Label>
              <Select value={title} onValueChange={setTitle}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {memberTitles.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('members.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1234567890"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('members.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="joinDate">{t('members.joinDate')}</Label>
              <Input
                id="joinDate"
                type="date"
                value={joinDate}
                onChange={(e) => setJoinDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t('members.status')}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as 'active' | 'inactive')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('members.active')}</SelectItem>
                  <SelectItem value="inactive">{t('members.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setIsAddDialogOpen(false)}
              >
                {t('app.cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('app.save')
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

